import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import type { InformationState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { useFieldEditable } from "../flow-editor/flow-readonly-context";
import {
  continueButtonTextFieldSpec,
  headerFieldSpec,
  htmlMessageFieldSpec,
} from "./translations/field-specs";
import { FlowFieldTranslations } from "./translations/flow-field-translations";
import { StepTranslationLanguages } from "./translations/step-translation-languages";
import {
  stepElementIdsOf,
  StepTranslationElement,
} from "./translations/step-translations";
import { TranslatableTextareaField } from "./translations/translatable-textarea-field";

export const InformationDetails = memo(function InformationDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<InformationState>) {
  const { t } = useTranslation();
  const canEditReturnsSettings = useFieldEditable(
    useHasPermission(SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS),
  );
  const translationElements = useMemo<StepTranslationElement[]>(
    () => [
      { elementId: state.stepUUID, field: headerFieldSpec(state.header) },
      { elementId: state.stepUUID, field: htmlMessageFieldSpec(state.message) },
      {
        elementId: state.stepUUID,
        field: continueButtonTextFieldSpec(state.continueButtonText),
      },
    ],
    [state.stepUUID, state.header, state.message, state.continueButtonText],
  );
  const stepElementIds = useMemo(
    () => stepElementIdsOf(translationElements),
    [translationElements],
  );

  return (
    <>
      <StepTranslationLanguages
        canEdit={canEditReturnsSettings}
        elements={translationElements}
        flowType={flowType}
      />
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Title")}
        </Text>
        <RedoTextInput
          errorText={!state.header ? t("Required") : undefined}
          placeholder={t("Enter title...")}
          setValue={(header) => setState((state) => ({ ...state, header }))}
          state={canEditReturnsSettings ? "default" : "readonly"}
          value={state.header}
        />
        <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
          {t("Title to show to the customer.")}
        </Text>
        <FlowFieldTranslations
          canEdit={canEditReturnsSettings}
          elementId={state.stepUUID}
          field={headerFieldSpec(state.header)}
          flowType={flowType}
          stepElementIds={stepElementIds}
        />
      </Flex>
      <TranslatableTextareaField
        canEdit={canEditReturnsSettings}
        elementId={state.stepUUID}
        errorText={!state.message ? t("Required") : undefined}
        field={htmlMessageFieldSpec(state.message)}
        flowType={flowType}
        helperText={t("Message to show to the customer. Can include HTML.")}
        placeholder={t("Enter message...")}
        setValue={(message) => setState((state) => ({ ...state, message }))}
        stepElementIds={stepElementIds}
      />
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Continue Button Text")}
        </Text>
        <RedoTextInput
          errorText={!state.continueButtonText ? t("Required") : undefined}
          placeholder={t("Enter button text...")}
          setValue={(continueButtonText) =>
            setState((state) => ({ ...state, continueButtonText }))
          }
          state={canEditReturnsSettings ? "default" : "readonly"}
          value={state.continueButtonText}
        />
        <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
          {t("Text to show on the continue button.")}
        </Text>
        <FlowFieldTranslations
          canEdit={canEditReturnsSettings}
          elementId={state.stepUUID}
          field={continueButtonTextFieldSpec(state.continueButtonText)}
          flowType={flowType}
          stepElementIds={stepElementIds}
        />
      </Flex>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Next step")}
        </Text>
        {stepSelect({
          value: state.next,
          valueChange: (next) => setState((state) => ({ ...state, next })),
          handleIndex: 0,
        })}
        {!state.next && (
          <Text fontSize="xs" textColor="error">
            {t("Required")}
          </Text>
        )}
      </Flex>
    </>
  );
});
